import React, { useState } from "react";

import { Box, Button, CircularProgress, Divider, Grid, IconButton, Menu, MenuItem, Slider, Typography } from "@mui/material";
import { Add, Edit, MoreVert } from "@mui/icons-material";

import { DataLayout, FieldControl, StatusChip } from "../../../shared/components";
import { ExpandableText } from "../../../shared/components/ExpandableText";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";

interface RaiseAssessmentProps {
    impactData: any;
    isAssessmentFetching: boolean;
    isAssessmentPending: boolean;
    isSaving: boolean;
    assessmentData: any;
    setImpactData: (data: any) => void;
    onSaveImpact: () => void;
    onClearImpact: () => void;
    editImpactStatus: any;
    onEditImpact: (impact: any) => void;
}

export const RaiseAssessment: React.FC<RaiseAssessmentProps> = ({ impactData, isAssessmentFetching, isAssessmentPending, isSaving, assessmentData, setImpactData, onSaveImpact, onClearImpact, editImpactStatus, onEditImpact }) => {

    const { isAllowedAddAssessment, isAllowedEditAssessment } = useCapabilities();

    const [formStatus, setFormStatus] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { getFilterOptions } = useDisplayField([]);

    const handleFieldChange = (key: string, value: any) => {
        setImpactData({ ...impactData, [key]: value });
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!impactData.impactTitle) newErrors.impactTitle = "Impact title is required";
        if (!impactData.impactStatus) newErrors.impactStatus = "Status is required";
        if (!impactData.impactDescription) newErrors.impactDescription = "Description is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<any>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cause: any) => {
        event.stopPropagation();
        setSelected(cause);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (isAssessmentFetching || isAssessmentPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} sx={{ pr: 1 }}>
            {
                !formStatus && isAllowedAddAssessment && (
                    <Button onClick={() => { setErrors({}); setFormStatus(true); }} variant="outlined">Add Assessment</Button>
                )
            }
            {
                formStatus && (
                    <DataLayout title={"Add Impact"} bgVariant="filled">
                        <Grid container spacing={2} p={2}>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactTitle"}
                                    label={"Impact Title"}
                                    variant={"input"}
                                    value={impactData.impactTitle}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactTitle}
                                    helperText={errors.impactTitle}
                                    required
                                />
                            </Grid>
                            <Grid size={6}>
                                <FieldControl
                                    keyName={"impactStatus"}
                                    label={"Status"}
                                    variant={"select"}
                                    options={getFilterOptions('RISK_STATUS').map((value) => {
                                        return {
                                            label: value.display_text,
                                            value: value.key
                                        }
                                    })}
                                    value={impactData.impactStatus}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactStatus}
                                    helperText={errors.impactStatus}
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <FieldControl
                                    keyName={"impactDescription"}
                                    label={"Description"}
                                    variant={"input"}
                                    multiline
                                    value={impactData.impactDescription}
                                    onChange={handleFieldChange}
                                    error={!!errors.impactDescription}
                                    helperText={errors.impactDescription}
                                    required
                                />
                            </Grid>
                            <Grid size={12} container border={1} borderColor={'divider'} borderRadius={1} padding={2} spacing={2}>
                                <Grid size={12} display={'flex'} alignItems={'center'} gap={1}>
                                    <Typography variant="subtitle2" color="text.primary">Current Impact</Typography>
                                    <Box ml={'auto'} display={'flex'} alignItems={'center'} gap={1}>
                                        <Typography variant="caption" color="text.secondary">Score</Typography>
                                        <StatusChip label={String(Number(impactData.impact) * Number(impactData.probability))} color="amber" variant="filled" sx={{ borderRadius: '4px', px: .5 }} />
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Divider />
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Impact</Typography>
                                        <Slider
                                            value={impactData.impact}
                                            onChange={(_, value) =>
                                                setImpactData({ ...impactData, impact: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{ px: 2 }}>
                                        <Typography variant="subtitle2" color="text.primary">Probability</Typography>
                                        <Slider
                                            value={impactData.probability}
                                            onChange={(_, value) =>
                                                setImpactData({ ...impactData, probability: value as number })
                                            }
                                            valueLabelDisplay="auto"
                                            step={1}
                                            marks
                                            min={1}
                                            max={5}
                                            sx={{ mt: 2 }}
                                        />
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                            {
                                                getFilterOptions('RISK_SCORE').map((value) => (
                                                    <Typography variant="caption">{value.display_text}</Typography>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                            <Grid size={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
                                <Button
                                    sx={{ color: 'text.primary' }}
                                    onClick={() => {
                                        onClearImpact();
                                        setErrors({});
                                        setFormStatus(false);
                                    }}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    startIcon={<Add />}
                                    onClick={() => {
                                        if (validate()) {
                                            onSaveImpact();
                                        }
                                    }}
                                    disabled={isSaving}
                                    loading={isSaving}
                                >
                                    {editImpactStatus.editImpactStatus ? 'Edit assessment' : 'Add assessment'}
                                </Button>
                            </Grid>
                        </Grid>
                    </DataLayout>
                )
            }
            <Box display={'flex'} flexDirection={'column'} gap={2} mt={2}>
                {
                    assessmentData.length === 0 ?
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={5}>No assessments found.</Typography> :
                        assessmentData.map((assessment: any) => (
                            <Box display={'flex'} gap={1} key={assessment.id} border={1} borderColor={'divider'} flexWrap={'wrap'} justifyContent={'space-between'} alignItems={'flex-start'} borderRadius={1} padding={2}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'start'} gap={.5} flex={1}>
                                    <ExpandableText 
                                        text={assessment.title || 'N/A'} 
                                        variant="subtitle2" 
                                        color="text.primary"
                                        maxLines={1} 
                                        maxWidth="100%" 
                                    />
                                    <ExpandableText 
                                        text={assessment.description || 'N/A'} 
                                        variant="caption" 
                                        color="text.secondary"
                                        maxLines={2} 
                                        maxWidth="100%" 
                                    />
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5}>
                                    <StatusChip label={assessment.status_display_text || 'N/A'} color="amber" />
                                    {
                                        isAllowedEditAssessment && (
                                            <IconButton onClick={(e) => handleMenuOpen(e, assessment)}>
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                </Box>
                                <Box display={'flex'} alignItems={'center'} gap={.5} width={'100%'}>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={assessment.impact || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={assessment.probability || 'N/A'} variant="filled" color="amber" sx={{ borderRadius: '4px', mr: 2, ml: 1 }} />
                                </Box>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onEditImpact(selected);
                                            setErrors({});
                                            setFormStatus(true);
                                            handleMenuClose();
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography ml={1}>Edit</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                        ))
                }
            </Box>
        </Box>
    )
}